/**
 * Normalizes filter values to match the case-sensitive format stored in Algolia.
 * This ensures that URLs with different cases (e.g., from search engine crawlers)
 * still match the correct filters.
 */

// Known filter values stored in Algolia (case-sensitive)
// Using an array to avoid duplicate keys (Hybrid appears in both English and Swedish)
const REMOTENESS_VALUES = [
  // English
  'Remote',
  'Hybrid',
  'Onsite',
  // Swedish
  'På plats',
  'Distans',
  // Note: 'Hybrid' is the same for English and Swedish
  // Finnish
  'Etätyö',
  'Hybridityö',
  'Paikkasidonnainen työ',
] as const;

// Create a case-insensitive lookup map for remoteness
const REMOTENESS_LOOKUP = new Map<string, string>();
REMOTENESS_VALUES.forEach(value => {
  REMOTENESS_LOOKUP.set(value.toLowerCase(), value);
  // Also add common variations
  if (value.toLowerCase().includes('etätyö')) {
    REMOTENESS_LOOKUP.set('etätyö', 'Etätyö');
    REMOTENESS_LOOKUP.set('etatyö', 'Etätyö');
    REMOTENESS_LOOKUP.set('etätyo', 'Etätyö');
    REMOTENESS_LOOKUP.set('etatyo', 'Etätyö');
  }
  if (value.toLowerCase().includes('hybridityö')) {
    REMOTENESS_LOOKUP.set('hybridityö', 'Hybridityö');
    REMOTENESS_LOOKUP.set('hybridityo', 'Hybridityö');
  }
  if (value.toLowerCase().includes('paikkasidonnainen')) {
    REMOTENESS_LOOKUP.set('paikkasidonnainen työ', 'Paikkasidonnainen työ');
  }
  if (value.toLowerCase() === 'remote') {
    REMOTENESS_LOOKUP.set('remote', 'Remote');
  }
  if (value.toLowerCase() === 'hybrid') {
    REMOTENESS_LOOKUP.set('hybrid', 'Hybrid');
  }
  if (value.toLowerCase() === 'onsite') {
    REMOTENESS_LOOKUP.set('onsite', 'Onsite');
  }
  if (value.toLowerCase().includes('distans')) {
    REMOTENESS_LOOKUP.set('distans', 'Distans');
  }
  if (value.toLowerCase().includes('på plats')) {
    REMOTENESS_LOOKUP.set('på plats', 'På plats');
  }
});

/**
 * Normalizes a filter attribute key to lowercase.
 * This ensures case-insensitive parameter key matching.
 * @param key The parameter key (e.g., 'Remoteness', 'LOCATION')
 * @returns The normalized lowercase key (e.g., 'remoteness', 'location')
 */
export function normalizeFilterKey(key: string): string {
  return key.toLowerCase();
}

/**
 * Normalizes a filter value to match the case-sensitive format stored in Algolia.
 * For attributes with known fixed values (like remoteness), this will normalize to the correct case.
 * For other attributes, the value is returned as-is since we can't know all possible values.
 * 
 * @param attribute The filter attribute name (e.g., 'remoteness', 'location')
 * @param value The filter value from the URL (may be case-insensitive)
 * @returns The normalized value matching the stored format, or the original value if no match found
 */
export function normalizeFilterValue(attribute: string, value: string): string {
  // Normalize attribute name to lowercase for comparison
  const attrLower = attribute.toLowerCase();
  
  // Handle remoteness attribute (has known fixed values)
  if (attrLower === 'remoteness') {
    const normalized = REMOTENESS_LOOKUP.get(value.toLowerCase());
    if (normalized) {
      return normalized;
    }
    // If no exact match, try to find a partial match
    for (const [key, storedValue] of REMOTENESS_LOOKUP.entries()) {
      if (value.toLowerCase().includes(key) || key.includes(value.toLowerCase())) {
        return storedValue;
      }
    }
  }
  
  // For other attributes (location, companyIndustry, etc.), we preserve the original value
  // because:
  // 1. These values are dynamic and we can't know all possible values
  // 2. Algolia filters are case-sensitive, so exact case matching is required
  // 3. The key normalization (to lowercase) ensures the parameter is recognized
  // 
  // Note: If case-insensitive matching is needed for other attributes in the future,
  // we would need to query Algolia facets first to get the exact stored values,
  // which is expensive. For now, we rely on the key normalization to at least
  // ensure the parameter is recognized, even if the value case doesn't match.
  
  return value;
}
